import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribed — Like2Fit" },
      { name: "description", content: "You're unsubscribed from Like2Fit emails." },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    uid: typeof s.uid === "string" ? s.uid : undefined,
    token: typeof s.token === "string" ? s.token : undefined,
  }),
  component: UnsubscribePage,
});

type Status = "idle" | "working" | "done" | "error";

function UnsubscribePage() {
  const search = useSearch({ from: "/unsubscribe" });
  const uid = search.uid ?? search.token; // accept either param name
  const [status, setStatus] = useState<Status>("idle");

  useEffect(() => {
    if (!uid) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("working");
    (async () => {
      try {
        const { error } = await (supabase.from("profiles") as any)
          .update({ email_subscribed: false })
          .eq("id", uid);
        if (cancelled) return;
        if (error) {
          console.error("[unsubscribe] update failed", error);
          setStatus("error");
        } else {
          setStatus("done");
        }
      } catch (err) {
        console.error("[unsubscribe] threw", err);
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const heading =
    status === "done"
      ? "You're unsubscribed"
      : status === "error"
        ? "Something went wrong"
        : "Updating your preferences…";
  const body =
    status === "done"
      ? "You won't receive emails like this from Like2Fit."
      : status === "error"
        ? "We couldn't update your email preferences. Please reply to a recent email and we'll sort it out."
        : "One moment.";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 24px",
        textAlign: "center",
        background: "#fff",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 16 }}>
        Like2Fit
      </h1>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
        {heading}
      </h2>
      <p style={{ color: "#666", maxWidth: 360, lineHeight: 1.5, marginBottom: 28 }}>
        {body}
      </p>
      <a
        href="https://like2fit.com"
        style={{
          background: "#FF5C5C",
          color: "white",
          textDecoration: "none",
          padding: "12px 28px",
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        Open Like2Fit
      </a>
    </div>
  );
}
